import { useContext, useState, useEffect } from 'react';
import { DashboardContext } from '../DashboardContext';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { Typography, Stack, Button, Box, Modal, Tooltip, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { InsightsDateAggregation } from 'op-enums';
import BlurredChart from '../Assets/BlurredChart.png';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OverviewChart from './Charts/OverviewCharts/OverviewChart';
import OverviewDonutChart from './Charts/OverviewCharts/OverviewChartDonut';
import {
  getDeterministicOffset,
  isTreatmentAppointment,
  isSimAppointment,
  isSimAppointmentByString,
  isTreatmentAppointmentByString,
  filterOverviewAppointmentData,
} from '../Utils/utils';
import { LoadingSpinner } from 'shared-components/components';

const OverviewCard = (props: {
  title: string;
  type?: string;
  page: string;
  metricSuffix?: string;
  metricRound?: number;
  targetType?: string;
  enabled?: boolean;
  description?: string;
  modalImage?: any;
  dataType?: string;
  appointmentFilterFunction?: (appointmentActivities: string[], appointment: any) => boolean;
  loading: boolean;
  aggregateFunction: (
    data: any,
    aggregateBy: string | InsightsDateAggregation,
    tenantConfig?: any,
  ) => (string | number)[][];
  metricFunction: (data: any[], tenantLinacs?: any) => number;
}): JSX.Element => {
  const { filteredData, previousPeriodData, dateRange, tenantConfig, navToPage } = useContext(DashboardContext);
  const {
    title,
    type,
    page,
    metricSuffix = '',
    metricRound = 0,
    targetType,
    enabled,
    description,
    modalImage,
    dataType = 'CarepathCareplanType',
    appointmentFilterFunction,
    aggregateFunction,
    metricFunction,
    loading,
  } = props;

  const [data, setData] = useState<any[]>([]);
  const [previousData, setPreviousData] = useState<any[]>([]);
  const [aggregatedData, setAggregatedData] = useState<any[][]>([]);
  const [targetData, setTargetData] = useState<any[]>([]);
  const [metric, setMetric] = useState<string>('');
  const [previousPeriodMetric, setPreviousPeriodMetric] = useState<number>(0);
  const [metricChangeIndicator, setMetricChangeIndicator] = useState<string>('');
  const [isHovered, setIsHovered] = useState(false);
  const [dateAggregation, setDateAggregation] = useState<InsightsDateAggregation>(InsightsDateAggregation.DAYS);
  const [modalOpen, setModalOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const theme = useTheme();

  const handleClose = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    let data;
    let previousPeriodDataFiltered;

    if (dataType === 'AppointmentWithPractitionersType' && appointmentFilterFunction) {
      // Use the provided appointment filter function
      const getAppointmentActivities = () => {
        if (appointmentFilterFunction === isTreatmentAppointment) {
          return tenantConfig?.insightsTreatmentAppointmentActivities;
        } else if (appointmentFilterFunction === isSimAppointment) {
          return tenantConfig?.insightsSimAppointmentActivities;
        }
        return [];
      };

      const activities = getAppointmentActivities();

      const useAdvancedFiltering = tenantConfig?.insightsAdvancedAppointmentFiltering;

      if (appointmentFilterFunction === isSimAppointment && !useAdvancedFiltering) {
        // For clinics without advanced filtering, fall back to old string-based filtering for simulations
        const filtered = filterOverviewAppointmentData(filteredData, previousPeriodData, dataType, (appt: any) =>
          isSimAppointmentByString(appt, tenantConfig),
        );
        data = filtered.data;
        previousPeriodDataFiltered = filtered.previousPeriodDataFiltered;
      } else if (appointmentFilterFunction === isTreatmentAppointment && !useAdvancedFiltering) {
        // For clinics without advanced filtering, fall back to old string-based filtering for treatments
        const filtered = filterOverviewAppointmentData(filteredData, previousPeriodData, dataType, (appt: any) =>
          isTreatmentAppointmentByString(appt, tenantConfig),
        );
        data = filtered.data;
        previousPeriodDataFiltered = filtered.previousPeriodDataFiltered;
      } else {
        // For clinics with advanced filtering enabled, use new activity-based method
        data = filteredData.filter(
          (d: any) => d.__typename === dataType && appointmentFilterFunction(activities, d.appointment),
        );
        previousPeriodDataFiltered = previousPeriodData.filter(
          (d: any) => d.__typename === dataType && appointmentFilterFunction(activities, d.appointment),
        );
      }
    } else {
      // No appointment filtering needed - just filter by dataType
      data = filteredData.filter((d: any) => d.__typename === dataType);
      previousPeriodDataFiltered = previousPeriodData.filter((d: any) => d.__typename === dataType);
    }

    setData(data);
    setPreviousData(previousPeriodDataFiltered);
  }, [filteredData, tenantConfig, appointmentFilterFunction]);

  useEffect(() => {
    const aggregateType = type === 'donut' ? 'tumourStream' : dateAggregation;

    setAggregatedData(
      aggregateFunction.length === 2 || type === 'donut'
        ? aggregateFunction(data, aggregateType)
        : aggregateFunction(data, aggregateType, tenantConfig),
    );
    setMetric(
      metricFunction.length === 1
        ? metricFunction(data).toFixed(metricRound) + metricSuffix
        : metricFunction(data, tenantConfig).toFixed(metricRound) + metricSuffix,
    );

    const current = metricFunction.length === 1 ? metricFunction(data) : metricFunction(data, tenantConfig);
    const previous =
      metricFunction.length === 1 ? metricFunction(previousData) : metricFunction(previousData, tenantConfig);

    const percentChange = (Math.abs(current - previous) / previous) * 100;
    setPreviousPeriodMetric(percentChange);
    setMetricChangeIndicator(current > previous ? 'more' : 'less');
  }, [data]);

  useEffect(() => {
    // target data does not exist in the mock data at the moment. Making up values for it for now
    const average = aggregatedData.reduce((acc, point) => acc + point[1], 0) / aggregatedData.length;

    if (targetType === 'flat') {
      setTargetData(aggregatedData.map((point: any) => average));
    } else if (targetType === 'track') {
      setTargetData(aggregatedData.map((point: any) => getDeterministicOffset(point[1], average - 5, average + 5)));
    }
  }, [aggregatedData]);

  return (
    <Stack
      id="overview-chart-container"
      p={3}
      sx={{
        width: '400px',
        maxWidth: '400px',
        maxHeight: '272px',
        minHeight: '272px',
        borderColor: theme.palette.divider,
        borderWidth: '1px',
        borderStyle: 'solid',
        cursor: !!enabled ? 'pointer' : 'default',
      }}
      onClick={() => {
        return enabled && !loading ? navToPage(page) : null;
      }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom={0.5}>
        <Stack direction="row">
          <Typography
            style={{
              textDecoration: isHovered ? 'underline' : 'none',
            }}
            onMouseEnter={() => setIsHovered(true && !!enabled)}
            onMouseLeave={() => setIsHovered(false)}>
            {title}
          </Typography>
          <Tooltip
            title={description}
            open={showTooltip}
            placement="bottom"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  marginLeft: '14px',
                },
              },
            }}>
            <div style={{ width: 20 }}>
              {enabled && (
                <HelpOutlineIcon
                  style={{ marginLeft: '8px', width: '20px' }}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                />
              )}
            </div>
          </Tooltip>
        </Stack>
        {enabled && dateRange !== 'All time' && !isNaN(previousPeriodMetric) && isFinite(previousPeriodMetric) && (
          <ArrowForward
            style={{
              transform: `rotate(${metricChangeIndicator === 'more' ? '-' : ''}90deg)`,
            }}
          />
        )}
      </Stack>
      {enabled && (
        <Stack>
          {loading ? (
            <LoadingSpinner loadingText={''} />
          ) : (
            <Stack>
              <Typography variant="h5" sx={{ lineHeight: 1, marginBottom: '5px' }}>
                {metric}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '8px' }}>
                {dateRange !== 'All time' && !isNaN(previousPeriodMetric) && isFinite(previousPeriodMetric) && (
                  <Stack>
                    {previousPeriodMetric.toFixed(metricRound)}% {metricChangeIndicator} than previous{' '}
                    {dateRange.split('Past ')[1]}
                  </Stack>
                )}
              </Typography>
              {type == 'donut' ? (
                <OverviewDonutChart aggregatedData={aggregatedData.slice(1)} />
              ) : (
                <OverviewChart
                  aggregatedData={aggregatedData}
                  targetData={targetData}
                  yAxisSuffix={metricSuffix}
                  dateAggregation={dateAggregation || ''}
                />
              )}
            </Stack>
          )}
        </Stack>
      )}
      {!enabled && (
        <Stack>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          <Stack
            alignItems="center"
            justifyContent="center"
            position="relative"
            paddingBottom={1}
            sx={{ minHeight: 180, width: '100%' }}>
            <Box component="img" src={BlurredChart} position="absolute" />
            <Button
              onClick={() => setModalOpen(true)}
              sx={{
                position: 'absolute',
                borderRadius: '4px',
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}>
              <Typography color={theme.palette.primary.contrastText}>COMING SOON</Typography>
            </Button>
          </Stack>
        </Stack>
      )}
      <Modal open={modalOpen} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            maxWidth: '1300px',
            bgcolor: theme.palette.background.paper,
            borderRadius: 1,
            paddingTop: 2,
            paddingBottom: 4.5,
            minHeight: '800px',
          }}>
          <Stack direction="row">
            <Typography variant="h6" paddingLeft={4}>
              {title}
            </Typography>
            <Typography variant="h6" color={theme.palette.text.secondary} paddingLeft={0.5}>
              (Coming soon...)
            </Typography>
          </Stack>
          <Typography variant="body1" paddingLeft={4}>
            {description}
          </Typography>
          <Divider sx={{ margin: '20px 0' }} />
          <Stack justifyContent="center" alignItems="center" marginBottom={5}>
            <Box component="img" src={modalImage} />
            <Typography
              variant="h2"
              color={theme.palette.text.secondary}
              position="absolute"
              top="55%"
              left="50%"
              sx={{ transform: 'translate(-50%, -50%)' }}>
              Example
            </Typography>
          </Stack>
          <Divider sx={{ margin: '10px 0' }} />
          <Typography
            variant="button"
            position="absolute"
            onClick={handleClose}
            sx={{ right: '20px', cursor: 'pointer' }}>
            CLOSE
          </Typography>
        </Box>
      </Modal>
    </Stack>
  );
};

export default OverviewCard;
